import { FileUploaderDropContainer, FileUploaderItem } from '@carbon/react';
import type { MultiFileEntry } from 'lib/atoms/file-atom';
import styles from './related-items.module.scss';

// Mirrors `RelatedItem::ALLOWED_ATTACHMENT_TYPES`, or the picker offers files the
// model rejects.
export const ACCEPT = [
  '.pdf',
  '.csv',
  '.xlsx',
  '.xls',
  '.docx',
  '.doc',
  '.jpg',
  '.jpeg',
  '.tiff',
  '.tif',
  '.png',
  '.gif',
  '.msg',
  '.eml',
];

// RubyXL opens the OOXML package only; the full list let an unreadable file through.
export const WORKBOOK_ACCEPT = ['.xlsx'];

interface FileDropFieldProps {
  files: MultiFileEntry[];
  accept?: string[];
  onAdd: (files: File[]) => void;
  onRemove: (localId: string) => void;
}

/**
 * Drop zone, format hint and the list of files picked so far. Shared by the
 * uploaded-file and workbook flows, which differ in what they do with the blob
 * afterwards but not in how a person hands it over.
 *
 * @param {FileDropFieldProps} props - files already picked and the two callbacks
 * @return {JSX.Element} the drop zone and its file list
 */
export const FileDropField = ({
  files,
  onAdd,
  onRemove,
  accept = ACCEPT,
}: FileDropFieldProps): JSX.Element => (
  <>
    <FileUploaderDropContainer
      labelText="Drag and drop files here or click to upload"
      multiple
      accept={accept}
      onAddFiles={(_evt: unknown, { addedFiles }: { addedFiles: File[] }) => {
        // Size is the file atom's job: it marks an oversize file invalid and the
        // item below renders that on the file's own row.
        if (addedFiles?.length) onAdd(addedFiles);
      }}
    />
    <p className={styles.uploadHelper}>
      Supported formats: PDF, CSV, Excel, Word, JPG, TIFF, Outlook (.msg), Email
      (.eml) · Max size: 500MB
    </p>
    {files.map((f) => (
      <FileUploaderItem
        key={f.localId}
        name={f.name}
        status={f.status === 'uploading' ? 'uploading' : 'complete'}
        errorBody={f.error}
        invalid={f.invalid}
        onDelete={() => onRemove(f.localId)}
        size="sm"
      />
    ))}
  </>
);
